import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Navigate, Route, Routes, matchPath, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, Building2, CheckCircle2, ChevronRight, EllipsisVertical } from "lucide-react";
import { DpTierBadge } from "./OrgProfile";
import { useBranding } from "../context/BrandingContext";
import { WhatsNewPage } from "./WhatsNew";
import { theme, card, btnPrimary, countTag, floatingSheet, emptyFrame } from "../theme";
import { gbpMo, HARDENING_BENCHMARK_PENCE } from "../lib/pricing";
import { fmtDate, fmtGbp } from "../utils/format";
import { api, ApiError, startRoleSwitch, setViewingOrgId, apiErrorMessage } from "../api/client";
import { ActionsTile } from "../components/ActionsTile";
import { InviteClientModal, InviteStaffModal } from "../components/InviteModals";
import { InviteHistoryTable, revokeInvite } from "../components/InviteHistoryTable";
import type { InviteSummary } from "../components/InviteHistoryTable";
import { PlatformAdminPage } from "./PlatformAdminPage";
import { ConsultantUsersPage } from "./ConsultantUserModal";
import { ConsultantSsoPage } from "./ConsultantSsoModal";
import { LoadingBlock, ErrorBlock } from "../components/Spinner";
import { ConsultantAiSettingsPage } from "./ConsultantAiSettings";
import { FREE_REFERENCE_FRAMEWORKS, hasPaidStandard, withoutOrphanedReferenceFrameworks, type FrameworkId } from "../context/FrameworksContext";
import { ConsultantRatesPage } from "./ConsultantRates";
import { ConsultantBrandingPage } from "./ConsultantWhiteLabel";
import { DirectorySyncPanel } from "../components/DirectorySyncPanel";
import { SignInPolicyPanel } from "../components/SignInPolicyPanel";
import { FitText } from "../components/FitText";
import { ConsultantGroupsTab } from "./ConsultantGroups";
import { AppShell } from "../nav/AppShell";
import { Breadcrumb } from "../nav/AppTopBar";
import { useRouterLinkAdapter } from "../nav/LinkAdapter";
import { frameworkLabel } from "../nav/frameworkMeta";
import { geographyLabel } from "../lib/geography";
import {
  buildConsultantNav, clientOverviewPath, CONSULTANT_HOME, forgetRecentClientIds, pushRecentClientId, readRecentClientIds, RECENT_CLIENTS_MAX,
} from "../nav/consultantNav";
import type { ConsultantNavClient, RecentClient } from "../nav/consultantNav";
import type { NavModel, PaletteEntry } from "../nav/types";
import type { CurrentUser } from "../auth/useCurrentUser";
import { ClientSwitcher } from "./consultant/ClientSwitcher";
import { ConsultantClientPortal } from "./consultant/ConsultantClientPortal";
import { ConsultantClientRoute } from "./consultant/ConsultantClientRoute";
import { rankActions } from "./ActionsPage";
import { ConsultantShellContext, useConsultantShell } from "./consultant/ConsultantShellContext";
import type { ConsultantShellValue, PortfolioState } from "./consultant/ConsultantShellContext";
import { scoreColor, scoreLabel } from "./consultant/types";
import { scoreBand, isCompliantScore, clientNeedsAction, SCORE_BAND_LABELS } from "../lib/portfolioStatus";
import type { ClientLite, ConsultantTab, OrgSummary, PortfolioResponse } from "./consultant/types";
import { useConsultantSwitches } from "./consultant/useConsultantSwitches";
import { CalendarTab, KIND_STYLE, type ConsultantEvent } from "./consultant/CalendarTab";
import type { Scope } from "./consultant/workspace";
import { TodayPage } from "./consultant/TodayPage";
import { FindingsPage } from "./consultant/FindingsPage";
import { ActivityPage } from "./consultant/ActivityPage";
import { ClientsTable } from "./consultant/ClientsTable";
import { PipelinePanel } from "./consultant/PipelinePanel";
import { ScopeToggle } from "./consultant/ScopeToggle";
import { RevenuePage, TimePage, UtilisationPage } from "./consultant/PracticePages";
import { auditActionLabel } from "../lib/auditAction";

// ── Consultant workspace ──────────────────────────────────────────────────
// URL-routed under /consultant/* (docs/NAVIGATION.md section 1 and 5). The
// page components below are the former tabs; ConsultantWorkspace gives each a
// route inside AppShell, ConsultantClientRoute is the client management view
// and pages/consultant/ConsultantClientPortal the read-through. The shared
// state (client list, portfolio, recent clients, role switches, dialogs) lives
// in ConsultantPortfolioPage and reaches every route through
// ConsultantShellContext.

export type { OrgSummary, PortfolioSummary, ClientLite, ConsultantTab } from "./consultant/types";

// ── Sub-components ─────────────────────────────────────────────────────────

function SoaBar({ stats }: { stats: OrgSummary["soaStats"] }) {
  const total = stats.compliant + stats.partial + stats.gap;
  if (total === 0) return <div style={{ height: 6, background: theme.color.border, borderRadius: 3 }} />;
  return (
    <div style={{ display: "flex", height: 6, borderRadius: 3, overflow: "hidden", gap: 1 }}>
      <div style={{ flex: stats.compliant, background: theme.color.success }} />
      <div style={{ flex: stats.partial, background: theme.color.warning }} />
      <div style={{ flex: stats.gap, background: theme.color.danger }} />
    </div>
  );
}

function StatTile({
  label, value, sub, color,
}: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div style={{ ...card, flex: 1, minWidth: 140, display: "flex", flexDirection: "column" }}>
      <div style={{ fontSize: 10, letterSpacing: "0.08em", color: theme.color.faint, textTransform: "uppercase", marginBottom: 8 }}>
        {label}
      </div>
      {/* Pushed to the bottom so the values of a row line up even when one label wraps. */}
      <div style={{ marginTop: "auto" }}>
        <FitText size={28} style={{ fontWeight: 700, color: color ?? theme.color.heading, lineHeight: 1 }}>
          {value}
        </FitText>
        {sub && <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 4 }}>{sub}</div>}
      </div>
    </div>
  );
}

function ClientCard({
  org, onClick, isSelf, onMarkSelf, onUnmarkSelf, onRemove,
}: {
  org: OrgSummary;
  onClick: () => void;
  isSelf: boolean;
  onMarkSelf: () => void;
  onUnmarkSelf: () => void;
  onRemove: (id: string, name: string) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const color = scoreColor(org.latestScore);
  const total = org.soaStats.compliant + org.soaStats.partial + org.soaStats.gap;

  return (
    <div
      style={{
        ...card,
        display: "flex", flexDirection: "column", gap: 12,
        border: `1px solid ${isSelf ? theme.color.accent : hovered ? theme.color.accent : theme.color.border}`,
        transition: "border-color 0.15s",
        position: "relative",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setMenuOpen(false); }}
    >
      <div
        onClick={onClick}
        style={{ display: "flex", flexDirection: "column", gap: 12, cursor: "pointer" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 240 }}>{org.name}</span>
                {isSelf && (
                  <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: theme.color.accentBg, color: theme.color.accentText, fontWeight: 600 }}>
                    Your company
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span>{[org.sector, geographyLabel(org.geography), org.size ? `${org.size} staff` : null].filter(Boolean).join(" · ") || "No profile details"}</span>
                {org.dpTier && <DpTierBadge tier={org.dpTier} />}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexShrink: 0 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1 }}>
                {org.latestScore !== null ? `${org.latestScore}%` : "—"}
              </div>
              <div style={{ fontSize: 10, color: theme.color.subtle, marginTop: 2 }}>{scoreLabel(org.latestScore)}</div>
            </div>
            <div style={{ position: "relative" }}>
              <button
                onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: theme.color.muted, fontSize: 18, lineHeight: 1,
                  padding: "0 4px", marginTop: 2,
                }}
                title="More options"
                aria-label="More options"
              >
                <EllipsisVertical size={16} />
              </button>
              {menuOpen && (
                <div
                  style={{
                    position: "absolute", right: 0, top: "100%", zIndex: 100,
                    ...floatingSheet,
                    minWidth: 220, overflow: "hidden",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {!confirmRemove ? (
                    <>
                      {isSelf ? (
                        <button
                          onClick={() => { onUnmarkSelf(); setMenuOpen(false); }}
                          style={{
                            display: "block", width: "100%", textAlign: "left",
                            padding: "10px 14px", fontSize: 13, cursor: "pointer",
                            background: "none", border: "none", color: theme.color.text,
                          }}
                        >
                          Remove &lsquo;Your company&rsquo; label
                        </button>
                      ) : (
                        <button
                          onClick={() => { onMarkSelf(); setMenuOpen(false); }}
                          style={{
                            display: "block", width: "100%", textAlign: "left",
                            padding: "10px 14px", fontSize: 13, cursor: "pointer",
                            background: "none", border: "none", color: theme.color.text,
                          }}
                        >
                          Mark as your company
                        </button>
                      )}
                      <div style={{ borderTop: `1px solid ${theme.color.border}` }} />
                      <button
                        onClick={() => setConfirmRemove(true)}
                        style={{
                          display: "block", width: "100%", textAlign: "left",
                          padding: "10px 14px", fontSize: 13, cursor: "pointer",
                          background: "none", border: "none", color: theme.color.dangerText,
                        }}
                      >
                        Remove client…
                      </button>
                    </>
                  ) : (
                    <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
                      <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.4 }}>
                        Permanently delete <strong style={{ color: theme.color.heading }}>{org.name}</strong> and all their data? This cannot be undone.
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          onClick={() => { setConfirmRemove(false); setMenuOpen(false); onRemove(org.id, org.name); }}
                          style={{
                            flex: 1, padding: "6px 0", borderRadius: 3, border: "none",
                            background: theme.color.danger, color: "#fff",
                            fontSize: 12, fontWeight: 700, cursor: "pointer",
                          }}
                        >
                          Delete
                        </button>
                        <button
                          onClick={() => { setConfirmRemove(false); setMenuOpen(false); }}
                          style={{
                            flex: 1, padding: "6px 0", borderRadius: 3,
                            border: `1px solid ${theme.color.border}`,
                            background: "none", color: theme.color.muted,
                            fontSize: 12, cursor: "pointer",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <SoaBar stats={org.soaStats} />

        {total > 0 && (
          <div style={{ display: "flex", gap: 16, fontSize: 11 }}>
            <span style={{ color: theme.color.successText }}>{org.soaStats.compliant} Compliant</span>
            <span style={{ color: theme.color.warningText }}>{org.soaStats.partial} Partial</span>
            <span style={{ color: theme.color.dangerText }}>{org.soaStats.gap} Gap</span>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <span
              style={{
                fontSize: 10, padding: "2px 8px", borderRadius: 4,
                background: theme.color.consultantBg, color: theme.color.consultant, fontWeight: 600,
              }}
            >
              {org.serviceTier.name}
            </span>
            {(org.bundleTier || org.smallBusinessPackage) && (
              <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: theme.color.successBg, color: theme.color.successText, fontWeight: 600 }}>
                {org.smallBusinessPackage ? "CE Annual" : org.bundleTier === "small" ? "Small Biz Bundle" : org.bundleTier === "medium" ? "Medium Biz Bundle" : org.bundleTier === "large" ? "Large Biz Bundle" : "Enterprise Bundle"}
              </span>
            )}
            {org.frameworks.length === 0 ? (
              <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: theme.color.warningBg, color: theme.color.warningText, fontWeight: 600 }}>
                No frameworks
              </span>
            ) : org.frameworks.map((f) => (
              <span key={f.frameworkId} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: theme.color.accentBg, color: theme.color.accentText, fontWeight: 600 }}>
                {frameworkLabel(f.frameworkId)}
              </span>
            ))}
            {org.userCount === 0 && (
              <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: theme.color.panelAlt, color: theme.color.faint, fontWeight: 600 }}>
                No users
              </span>
            )}
            {org.openFindings > 0 && (
              <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: theme.color.dangerBg, color: theme.color.dangerText, fontWeight: 600 }}>
                {org.openFindings} finding{org.openFindings !== 1 ? "s" : ""}
              </span>
            )}
            {org.pendingTierRequests > 0 && (
              <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: theme.color.warningBg, color: theme.color.warningText, fontWeight: 600 }}>
                Tier request
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: theme.color.faint }}>
            {org.lastChecked ? `Assessed ${fmtDate(org.lastChecked)}` : "Never assessed"}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Cross-portfolio risk aggregation tiles ────────────────────────────────

interface PortfolioRiskClient {
  orgId: string;
  orgName: string;
  totalRisks: number;
  openRisks: number;
  criticalRisks: number;
}

interface PortfolioTopRisk {
  id: string;
  orgId: string;
  orgName: string;
  title: string;
  category: string;
  status: string;
  residualScore: number | null;
  inherentScore: number | null;
  displayId: number | null;
  priority: string | null;
}

interface PortfolioRisks {
  totalRisks: number;
  openRisks: number;
  criticalRisks: number;
  frameworkDistribution: Array<{ frameworkId: string; orgCount: number; riskCount: number }>;
  perClient: PortfolioRiskClient[];
  topRisks: PortfolioTopRisk[];
}

const PRIORITY_COLOR: Record<string, string> = {
  critical: theme.color.dangerText,
  high:     theme.color.riskHighText,
  medium:   theme.color.warningText,
  low:      theme.color.successText,
};

function PortfolioRiskTiles({ onSelect }: { onSelect: (orgId: string, orgName: string) => void }) {
  const [data, setData] = useState<PortfolioRisks | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<PortfolioRisks>("/consultant/portfolio/risks")
      .then(setData)
      .catch((e) => setError(e instanceof Error && e.message ? e.message : "Could not load the portfolio risk overview"));
  }, []);

  if (error) return <div style={{ fontSize: 12.5, color: theme.color.dangerText, marginBottom: 16 }}>Portfolio risk overview unavailable: {error}</div>;
  if (!data || data.totalRisks === 0) return null;

  const sorted = [...data.frameworkDistribution].sort((a, b) => b.riskCount - a.riskCount);

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
        Portfolio Risk Overview
      </div>

      {/* Aggregate tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 12, marginBottom: 16 }}>
        <StatTile label="Total Risks" value={String(data.totalRisks)} sub="across all clients" />
        <StatTile
          label="Open / In Treatment"
          value={String(data.openRisks)}
          sub="not yet resolved"
          color={data.openRisks > 0 ? theme.color.warningText : theme.color.successText}
        />
        <StatTile
          label="Critical Residual"
          value={String(data.criticalRisks)}
          sub="residual score > 16"
          color={data.criticalRisks > 0 ? theme.color.dangerText : theme.color.successText}
        />
        {sorted.slice(0, 4).map((fw) => (
          <StatTile
            key={fw.frameworkId}
            label={frameworkLabel(fw.frameworkId)}
            value={String(fw.riskCount)}
            sub={`${fw.orgCount} org${fw.orgCount !== 1 ? "s" : ""}`}
          />
        ))}
      </div>

      {/* Per-client breakdown + top 3 risks side by side */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>

        {/* Risks per client */}
        {data.perClient.length > 0 && (
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${theme.color.border}`, fontSize: 11, fontWeight: 700, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Risks by Client
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--c-line)" }}>
                  {["Client", "Total", "Open", "Critical"].map((h) => (
                    <th key={h} style={{ padding: "7px 12px", textAlign: h === "Client" ? "left" : "center", fontSize: 10, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.perClient.map((c, i) => (
                  <tr
                    key={c.orgId}
                    onClick={() => onSelect(c.orgId, c.orgName)}
                    style={{ borderBottom: i < data.perClient.length - 1 ? `1px solid ${theme.color.border}` : "none", cursor: "pointer" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = theme.color.accentBg)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <td style={{ padding: "8px 12px", color: theme.color.heading, fontWeight: 600, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.orgName}</td>
                    <td style={{ padding: "8px 12px", textAlign: "center", color: theme.color.text }}>{c.totalRisks}</td>
                    <td style={{ padding: "8px 12px", textAlign: "center", color: c.openRisks > 0 ? theme.color.warningText : theme.color.subtle, fontWeight: c.openRisks > 0 ? 700 : 400 }}>{c.openRisks}</td>
                    <td style={{ padding: "8px 12px", textAlign: "center", color: c.criticalRisks > 0 ? theme.color.dangerText : theme.color.subtle, fontWeight: c.criticalRisks > 0 ? 700 : 400 }}>{c.criticalRisks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Top 3 risks */}
        {data.topRisks.length > 0 && (
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${theme.color.border}`, fontSize: 11, fontWeight: 700, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Top Risks
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {data.topRisks.map((r, i) => {
                const score = r.residualScore ?? r.inherentScore ?? 0;
                const scoreColor = score > 16 ? theme.color.dangerText : score > 9 ? theme.color.warningText : theme.color.successText;
                const priorityColor = r.priority ? (PRIORITY_COLOR[r.priority] ?? theme.color.subtle) : theme.color.subtle;
                const isLast = i === data.topRisks.length - 1;
                return (
                  <div
                    key={r.id}
                    onClick={() => onSelect(r.orgId, r.orgName)}
                    style={{
                      padding: "12px 16px", cursor: "pointer",
                      borderBottom: isLast ? "none" : `1px solid ${theme.color.border}`,
                      display: "flex", flexDirection: "column", gap: 4,
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = theme.color.accentBg)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {r.displayId != null ? `RISK-${String(r.displayId).padStart(3, "0")} ` : ""}{r.title}
                        </div>
                        <div style={{ display: "flex", gap: 6, marginTop: 3, flexWrap: "wrap", alignItems: "center" }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); onSelect(r.orgId, r.orgName); }}
                            style={{ background: theme.color.accentBg, border: "none", borderRadius: 4, padding: "1px 7px", fontSize: 10, color: theme.color.accentText, cursor: "pointer", fontWeight: 600 }}
                          >
                            {r.orgName}
                          </button>
                          <span style={{ fontSize: 10, color: theme.color.faint }}>{r.category}</span>
                          {r.priority && (
                            <span style={{ fontSize: 10, color: priorityColor, fontWeight: 600, textTransform: "capitalize" }}>{r.priority}</span>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{score}</div>
                        <div style={{ fontSize: 9, color: theme.color.faint, marginTop: 1 }}>score</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Attention panel ───────────────────────────────────────────────────────

function AttentionPanel({ orgs, onSelect }: { orgs: OrgSummary[]; onSelect: (id: string, name: string) => void }) {
  const tierRequests  = orgs.filter((o) => o.pendingTierRequests > 0);
  const withFindings  = orgs.filter((o) => o.openFindings > 0);
  const critical      = orgs.filter((o) => scoreBand(o.latestScore) === "non_compliant");
  const unassessed    = orgs.filter((o) => o.lastChecked === null);
  const noFrameworks  = orgs.filter((o) => o.frameworks.length === 0);
  const noUsers       = orgs.filter((o) => o.userCount === 0 && o.frameworks.length > 0);

  const groups = [
    {
      key: "tier",
      color: theme.color.warningText,
      bg: theme.color.warningBg,
      border: theme.color.warning,
      label: "Pending tier upgrade requests",
      items: tierRequests,
      sub: (o: OrgSummary) => `${o.serviceTier.name} → ${o.requestedTierName ?? "?"}`,
    },
    {
      key: "findings",
      color: theme.color.dangerText,
      bg: theme.color.dangerBg,
      border: theme.color.danger,
      label: "Open auditor findings",
      items: withFindings,
      sub: (o: OrgSummary) => `${o.openFindings} finding${o.openFindings !== 1 ? "s" : ""}`,
    },
    {
      key: "critical",
      color: theme.color.dangerText,
      bg: theme.color.dangerBg,
      border: theme.color.danger,
      label: "Non-compliant SOA score",
      items: critical,
      sub: (o: OrgSummary) => `${o.latestScore}%`,
    },
    {
      key: "unassessed",
      color: theme.color.subtle,
      bg: theme.color.panel,
      border: theme.color.border,
      label: "No AI check yet",
      items: unassessed,
      sub: () => "No AI check run",
    },
    {
      key: "noFrameworks",
      color: theme.color.warningText,
      bg: theme.color.warningBg,
      border: theme.color.warning,
      label: "No frameworks enrolled",
      items: noFrameworks,
      sub: () => "Onboarding incomplete",
    },
    {
      key: "noUsers",
      color: theme.color.subtle,
      bg: theme.color.panel,
      border: theme.color.border,
      label: "No client users yet",
      items: noUsers,
      sub: () => "Invite client users",
    },
  ].filter((g) => g.items.length > 0);

  if (groups.length === 0) return null;

  const totalItems = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <div
      style={{
        ...card,
        borderLeft: `4px solid ${theme.color.warning}`,
        marginBottom: 8,
        padding: "18px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.warningText }}>
        {totalItems} item{totalItems !== 1 ? "s" : ""} need{totalItems === 1 ? "s" : ""} attention
      </div>

      {groups.map((group) => (
        <div key={group.key} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: group.color, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {group.label} ({group.items.length})
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {group.items.map((o) => (
              <button
                key={o.id}
                onClick={() => onSelect(o.id, o.name)}
                style={{
                  background: group.bg,
                  border: `1px solid ${group.border}33`,
                  borderRadius: 3,
                  padding: "6px 12px",
                  fontSize: 12,
                  color: group.color,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span style={{ fontWeight: 600, color: theme.color.heading }}>{o.name}</span>
                <span style={{ color: group.color }}>{group.sub(o)}</span>
                <span style={{ color: theme.color.faint }}>→</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Portfolio tab ─────────────────────────────────────────────────────────

type StatusFilter = "all" | "attention" | "unassessed" | "noncompliant" | "partial" | "compliant";

function PortfolioTab({
  orgs, onSelect, onInvite, onInviteStaff, selfOrgId, onSelfOrgChange, onSetupSelfOrg, onRemove,
}: {
  orgs: OrgSummary[];
  onSelect: (id: string, name: string) => void;
  onInvite: () => void;
  onInviteStaff: () => void;
  onSetupSelfOrg: () => void;
  selfOrgId: string | null;
  onSelfOrgChange: (id: string | null) => void;
  onRemove: (id: string, name: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [tierFilter, setTierFilter] = useState("all");

  const tiers = Array.from(
    new Map(orgs.map((o) => [o.serviceTier.id, o.serviceTier.name])).entries()
  );

  const filtered = orgs.filter((org) => {
    if (search && !org.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (tierFilter !== "all" && org.serviceTier.id !== tierFilter) return false;
    if (statusFilter === "attention") return clientNeedsAction(org);
    if (statusFilter === "unassessed") return scoreBand(org.latestScore) === "unassessed";
    if (statusFilter === "noncompliant") return scoreBand(org.latestScore) === "non_compliant";
    if (statusFilter === "partial") return scoreBand(org.latestScore) === "partial";
    if (statusFilter === "compliant") return isCompliantScore(org.latestScore);
    return true;
  });

  // lib/portfolioStatus.ts — the same rule as the sidebar's Clients badge.
  const attentionCount = orgs.filter(clientNeedsAction).length;

  const statusPills: { key: StatusFilter; label: string; count: number }[] = [
    { key: "all",          label: "All",            count: orgs.length },
    { key: "attention",    label: "Needs Action",    count: attentionCount },
    { key: "unassessed",   label: "Not Assessed",    count: orgs.filter((o) => scoreBand(o.latestScore) === "unassessed").length },
    { key: "noncompliant", label: "Non-Compliant",   count: orgs.filter((o) => scoreBand(o.latestScore) === "non_compliant").length },
    { key: "partial",      label: "Partial",         count: orgs.filter((o) => scoreBand(o.latestScore) === "partial").length },
    { key: "compliant",    label: "Compliant",       count: orgs.filter((o) => isCompliantScore(o.latestScore)).length },
  ];

  const pillColor: Record<StatusFilter, string> = {
    all:          theme.color.accent,
    attention:    theme.color.danger,
    unassessed:   theme.color.faint,
    noncompliant: theme.color.dangerText,
    partial:      theme.color.warning,
    compliant:    theme.color.success,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        {/* Search */}
        <input
          type="text"
          placeholder="Search clients…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: "1 1 200px", maxWidth: 280,
            background: theme.color.panel, border: `1px solid ${theme.color.border}`,
            borderRadius: 3, padding: "8px 12px", fontSize: 13,
            color: theme.color.text, outline: "none",
          }}
        />

        {/* Status pills */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
          {statusPills.map(({ key, label, count }) => {
            const active = statusFilter === key;
            const color = pillColor[key];
            // "Needs Action" is visually urgent whenever count > 0, even when not selected
            const urgent = key === "attention" && count > 0 && !active;
            return (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                style={{
                  padding: "6px 12px", borderRadius: 3, fontSize: 12, cursor: "pointer",
                  fontWeight: active || urgent ? 700 : 400,
                  border: `1px solid ${active || urgent ? color : theme.color.border}`,
                  background: active ? `${color}22` : urgent ? `${color}18` : "transparent",
                  color: active || urgent ? color : theme.color.muted,
                  transition: "all 0.15s",
                }}
              >
                {label}
                <span style={{ ...countTag, display: "inline-block", marginLeft: 6 }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Tier filter */}
        {tiers.length > 1 && (
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            style={{
              background: theme.color.panel, border: `1px solid ${theme.color.border}`,
              borderRadius: 3, padding: "7px 10px", fontSize: 12,
              color: theme.color.text, cursor: "pointer", outline: "none",
            }}
          >
            <option value="all">All tiers</option>
            {tiers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        )}

        <button onClick={onInvite} style={{ ...btnPrimary, fontSize: 12, flexShrink: 0 }}>
          + Invite Client
        </button>
        {!selfOrgId && (
          <button
            onClick={onSetupSelfOrg}
            style={{
              fontSize: 12, flexShrink: 0,
              padding: "8px 16px", borderRadius: 3, cursor: "pointer", fontWeight: 600,
              background: "transparent", border: `1px solid ${theme.color.consultant}`,
              color: theme.color.consultant,
            }}
            title="Add your own organisation as a client so you can manage your compliance too"
          >
            + Add our company
          </button>
        )}
        {selfOrgId && (
          <button
            onClick={onInviteStaff}
            style={{
              fontSize: 12, flexShrink: 0,
              padding: "8px 16px", borderRadius: 3, cursor: "pointer", fontWeight: 600,
              background: "transparent", border: `1px solid ${theme.color.border}`,
              color: theme.color.text,
            }}
          >
            + Invite Staff
          </button>
        )}
      </div>

      {/* Results */}
      {orgs.length === 0 ? (
        <div style={{ ...emptyFrame, color: theme.color.muted, fontSize: 13, padding: 40 }}>
          No client organisations yet. Use <strong>Invite Client</strong> above to onboard your first client.
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ ...emptyFrame, color: theme.color.muted, fontSize: 13, padding: 32 }}>
          No clients match the current filters.
          <button
            onClick={() => { setSearch(""); setStatusFilter("all"); setTierFilter("all"); }}
            style={{ marginLeft: 10, background: "none", border: "none", color: theme.color.accentText, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <>
          {filtered.length !== orgs.length && (
            <div style={{ fontSize: 12, color: theme.color.subtle }}>
              Showing {filtered.length} of {orgs.length} clients
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(440px, 1fr))", gap: 16 }}>
            {filtered.map((org) => (
              <ClientCard
                key={org.id}
                org={org}
                onClick={() => onSelect(org.id, org.name)}
                isSelf={org.id === selfOrgId}
                onMarkSelf={() => onSelfOrgChange(org.id)}
                onUnmarkSelf={() => onSelfOrgChange(null)}
                onRemove={onRemove}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Billing tab ───────────────────────────────────────────────────────────

function BillingTab({ orgs, onSelect }: { orgs: OrgSummary[]; onSelect: (id: string, name: string) => void }) {
  const totalBilled       = orgs.reduce((s, o) => s + (o.monthlyBilledPence ?? 0), 0);
  const totalPlatformFees = orgs.reduce((s, o) => s + (o.monthlyAbacasRevenuePence ?? 0), 0);
  const totalYourRevenue  = orgs.reduce((s, o) => s + (o.monthlyConsultancyRevenuePence ?? 0), 0);
  const totalDelivered    = orgs.reduce((s, o) => s + (o.monthlyConsultancyDeliveredPence ?? 0), 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* KPI tiles */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <StatTile label="Total Billed" value={fmtGbp(totalBilled)} sub="this month" color={theme.color.successText} />
        <StatTile label="Platform Fees" value={fmtGbp(totalPlatformFees)} sub="goes to Abacas" color={theme.color.muted} />
        <StatTile label="Your Revenue" value={fmtGbp(totalYourRevenue)} sub="consultancy allocation, billed" color={theme.color.consultant} />
        <StatTile label="Delivered" value={fmtGbp(totalDelivered)} sub="bookings and logged time" color={theme.color.muted} />
        <StatTile label="Active Clients" value={String(orgs.length)} sub="organisations" />
      </div>

      {/* Table */}
      <div style={{ ...card, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--c-line)" }}>
              {["Client", "Tier / Bundle", "Total/mo", "Platform Fee", "Your Revenue (allocation)", "Delivered"].map((h) => (
                <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 10, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orgs.map((org) => (
              <tr
                key={org.id}
                onClick={() => onSelect(org.id, org.name)}
                style={{ borderBottom: `1px solid ${theme.color.border}`, cursor: "pointer" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = theme.color.accentBg)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <td style={{ padding: "10px 12px", color: theme.color.heading, fontWeight: 600 }}>{org.name}</td>
                <td style={{ padding: "10px 12px", color: theme.color.consultant }}>
                  {org.serviceTier.name}
                  {(org.bundleTier || org.smallBusinessPackage) && (
                    <span style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px", borderRadius: 3, background: theme.color.successBg, color: theme.color.successText, fontWeight: 600 }}>
                      {org.smallBusinessPackage ? "CE Annual" : `${org.bundleTier} bundle`}
                    </span>
                  )}
                </td>
                <td style={{ padding: "10px 12px", color: theme.color.text }}>{fmtGbp(org.monthlyBilledPence ?? 0)}</td>
                <td style={{ padding: "10px 12px", color: theme.color.muted }}>{fmtGbp(org.monthlyAbacasRevenuePence ?? 0)}</td>
                <td style={{ padding: "10px 12px", color: theme.color.successText }}>{fmtGbp(org.monthlyConsultancyRevenuePence ?? 0)}</td>
                <td style={{ padding: "10px 12px", color: theme.color.muted }}>
                  {fmtGbp(org.monthlyConsultancyDeliveredPence ?? 0)}
                  {org.consultancyDeliveryFlag && org.consultancyDeliveryFlag !== "none" && (
                    <span style={{ marginLeft: 6, fontSize: 11, color: theme.color.warning }}>
                      {org.consultancyDeliveryFlag === "below" ? "below allocation" : "above allocation"}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Activity tab ──────────────────────────────────────────────────────────

interface ActivityEntry {
  id: string;
  organizationId: string | null;
  organizationName: string | null;
  userDisplayName: string | null;
  role: string;
  category: string;
  action: string;
  detail: string | null;
  createdAt: string;
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

const CATEGORY_COLOR: Record<string, string> = {
  auth:     "var(--c-accent)",
  review:   "var(--c-warning)",
  document: "var(--c-success)",
  settings: "var(--c-subtle)",
  audit:    "var(--c-consultant)",
  template: "var(--c-accent)",
  comment:  "var(--c-faint)",
  profile:  "var(--c-subtle)",
  schedule: "var(--c-accent)",
};

function categoryColor(cat: string) {
  return CATEGORY_COLOR[cat] ?? theme.color.faint;
}

// The Activity and Findings pages live in pages/consultant/ActivityPage.tsx and FindingsPage.tsx.
// ── Reports tab ───────────────────────────────────────────────────────────

function downloadReportCsv(orgs: OrgSummary[]) {
  const header = ["Client", "SOA Score (%)", "Controls Implemented", "Controls Total", "Gaps", "Score Band", "Last AI Check Score (%)", "Last AI Check"];
  const rows = orgs.map((o) => [
    o.name,
    o.latestScore !== null ? String(o.latestScore) : "",
    String(o.soaStats.compliant),
    String(o.soaStats.compliant + o.soaStats.partial + o.soaStats.gap),
    String(o.soaStats.gap ?? 0),
    scoreLabel(o.latestScore),
    o.lastCheckScore !== null ? String(o.lastCheckScore) : "",
    fmtDate(o.lastChecked) || "Never",
  ]);
  const csv = [header, ...rows].map((r) => r.map((v) => `"${v.replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `portfolio-report-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function ReportsTab({ orgs, onSelect }: { orgs: OrgSummary[]; onSelect: (id: string, name: string) => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Status table */}
      <div style={{ ...card, overflow: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>Compliance Status</div>
          <button
            onClick={() => downloadReportCsv(orgs)}
            style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "5px 12px", fontSize: 12, fontWeight: 600, color: theme.color.muted, cursor: "pointer" }}
          >
            Download CSV
          </button>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--c-line)" }}>
              {["", "Client", "Score", "Controls", "Score band", "Gaps", "Last Checked"].map((h) => (
                <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 10, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orgs.map((org) => {
              const sc = scoreColor(org.latestScore);
              return (
                <tr
                  key={org.id}
                  onClick={() => onSelect(org.id, org.name)}
                  style={{ borderBottom: `1px solid ${theme.color.border}`, cursor: "pointer" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = theme.color.accentBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <td style={{ padding: "10px 12px" }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: sc }} />
                  </td>
                  <td style={{ padding: "10px 12px", color: theme.color.heading, fontWeight: 600 }}>{org.name}</td>
                  <td style={{ padding: "10px 12px", color: sc, fontWeight: 700 }}>
                    {org.latestScore !== null ? `${org.latestScore}%` : "—"}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <div style={{ width: 80 }}>
                      <SoaBar stats={org.soaStats} />
                    </div>
                  </td>
                  <td style={{ padding: "10px 12px", color: theme.color.subtle }}>{scoreLabel(org.latestScore)}</td>
                  <td style={{ padding: "10px 12px", color: org.soaStats.gap > 0 ? theme.color.dangerText : theme.color.subtle }}>
                    {org.soaStats.gap}
                  </td>
                  <td style={{ padding: "10px 12px", color: theme.color.faint }}>
                    {fmtDate(org.lastChecked) || "Never"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Certification pipeline, from engagements, audit schedules and completions —
          not score bands (backend lib/certificationPipeline.ts). */}
      <PipelinePanel onOpen={(id) => onSelect(id, orgs.find((o) => o.id === id)?.name ?? "")} showTable />
    </div>
  );
}

// ── Auditor Accreditation tab ─────────────────────────────────────────────
// Allows consultancies that hold formal certification-body accreditation to
// register an Auditor Bureau and grant individual staff members access to the
// Auditor Portal.

interface AuditorBureau {
  id: string;
  name: string;
  accreditationBody: string | null;
  accreditation: string | null;
  country: string | null;
  contactEmail: string | null;
  website?: string | null;
  description?: string | null;
  bureauStatus: string;
  ownedByConsultancyId: string | null;
  linkedAt: string;
}

const ACCREDITATION_BODIES = ["UKAS", "Schellman", "BSI", "A2LA", "NQA", "Alcumus", "Bureau Veritas", "SGS", "Intertek", "Other"];

function AuditorAccreditationTab() {
  const [bureaus, setBureaus] = useState<AuditorBureau[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState({
    name: "", accreditationBody: "", accreditation: "",
    country: "", contactEmail: "", website: "", description: "",
  });

  function load() {
    setLoading(true);
    api.get<{ bureaus: AuditorBureau[] }>("/consultant/auditor-bureaus")
      .then((b) => { setBureaus(b.bureaus); })
      .catch(() => setError("Failed to load auditor accreditation data."))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.accreditationBody) {
      setFormError("Bureau name and accreditation body are required.");
      return;
    }
    setSubmitting(true);
    setFormError("");
    try {
      await api.post("/consultant/auditor-bureaus", {
        name: form.name.trim(),
        accreditationBody: form.accreditationBody,
        accreditation: form.accreditation.trim() || undefined,
        country: form.country.trim() || undefined,
        contactEmail: form.contactEmail.trim() || undefined,
        website: form.website.trim() || undefined,
        description: form.description.trim() || undefined,
      });
      setShowCreateForm(false);
      setForm({ name: "", accreditationBody: "", accreditation: "", country: "", contactEmail: "", website: "", description: "" });
      load();
    } catch {
      setFormError("Failed to create bureau. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // Edit contact details of a bureau the firm registered, or unlink one (PATCH / DELETE
  // /consultant/auditor-bureaus/:id). Name and accreditation are what the platform team
  // reviewed, so they are not editable here.
  const { user: shellUser } = useConsultantShell();
  const [editingBureau, setEditingBureau] = useState<string | null>(null);
  const [edit, setEdit] = useState({ country: "", contactEmail: "", website: "", description: "" });
  const [bureauError, setBureauError] = useState("");
  const startEdit = (b: AuditorBureau) => {
    setEditingBureau(b.id);
    setBureauError("");
    setEdit({ country: b.country ?? "", contactEmail: b.contactEmail ?? "", website: b.website ?? "", description: b.description ?? "" });
  };
  async function saveBureau(id: string) {
    setBureauError("");
    try {
      await api.patch(`/consultant/auditor-bureaus/${id}`, {
        country: edit.country.trim() || null, contactEmail: edit.contactEmail.trim() || "", website: edit.website.trim() || "", description: edit.description.trim() || null,
      });
      setEditingBureau(null);
      load();
    } catch (e) {
      setBureauError(e instanceof Error && e.message ? e.message : "Could not save the bureau");
    }
  }
  async function unlinkBureau(b: AuditorBureau) {
    if (!window.confirm(`Unlink ${b.name} from your consultancy? Staff lose Auditor Portal access through it. The bureau itself is not deleted.`)) return;
    setBureauError("");
    try {
      await api.del(`/consultant/auditor-bureaus/${b.id}`);
      load();
    } catch (e) {
      setBureauError(e instanceof Error && e.message ? e.message : "Could not unlink the bureau");
    }
  }

  const statusBadge = (status: string) => {
    const map: Record<string, { label: string; color: string; bg: string }> = {
      active:         { label: "Approved",         color: theme.color.successText, bg: theme.color.successBg },
      pending_review: { label: "Pending Approval", color: theme.color.warningText, bg: theme.color.warningBg },
      suspended:      { label: "Suspended",        color: theme.color.dangerText,  bg: theme.color.dangerBg },
    };
    const s = map[status] ?? { label: status, color: theme.color.muted, bg: theme.color.panel };
    return (
      <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 3, background: s.bg, color: s.color }}>
        {s.label}
      </span>
    );
  };

  const hasActiveBureau = bureaus.some(b => b.bureauStatus === "active");

  if (loading) return <div style={{ padding: 24, fontSize: 13, color: theme.color.muted }}>Loading…</div>;
  if (error) return <div style={{ padding: 24, fontSize: 13, color: theme.color.dangerText }}>{error}</div>;

  return (
    <div style={{ padding: "24px 24px 48px" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>Auditor Accreditation</div>
        <div style={{ fontSize: 13, color: theme.color.muted, lineHeight: 1.6, maxWidth: 600 }}>
          This section is for consultancies that hold formal accreditation to conduct third-party certification audits
          (e.g. ISO 27001 certification audits under UKAS or an equivalent body). Registering an Auditor Bureau
          allows your accredited staff to access the Auditor Portal to review client evidence and issue findings.
        </div>
        <div style={{
          marginTop: 12, padding: "10px 14px", borderRadius: theme.radius.md,
          background: theme.color.warningBg, border: `1px solid ${theme.color.warning}`, fontSize: 12, color: theme.color.warningText,
        }}>
          <AlertTriangle size={13} style={{ verticalAlign: "-2px", marginRight: 4 }} />Only register an Auditor Bureau if your firm holds valid accreditation from a recognised certification body.
          New bureaus are reviewed by the platform team before staff can access the Auditor Portal.
        </div>
      </div>

      {/* Registered bureaus */}
      <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, marginBottom: 10 }}>Your Auditor Bureaus</div>

      {bureaus.length === 0 ? (
        <div style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 20 }}>No auditor bureaus registered yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          {bureauError && <div role="alert" style={{ fontSize: 12.5, color: theme.color.dangerText }}>{bureauError}</div>}
          {bureaus.map(b => {
            const owned = b.ownedByConsultancyId === shellUser.consultancyId;
            const fieldStyle: React.CSSProperties = { width: "100%", boxSizing: "border-box", padding: "7px 9px", border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.sm, background: theme.color.bg, color: theme.color.text, fontSize: 13 };
            return (
              <div key={b.id} style={{ ...card, padding: "14px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, marginBottom: 4 }}>{b.name}</div>
                    {b.accreditationBody && (
                      <div style={{ fontSize: 12, color: theme.color.muted, marginBottom: 2 }}>
                        Accreditation body: {b.accreditationBody}
                        {b.accreditation ? ` — ${b.accreditation}` : ""}
                      </div>
                    )}
                    {b.contactEmail && <div style={{ fontSize: 12, color: theme.color.muted }}>{b.contactEmail}</div>}
                    {b.website && <div style={{ fontSize: 12, color: theme.color.muted }}>{b.website}</div>}
                  </div>
                  <div style={{ flexShrink: 0, display: "flex", gap: 8, alignItems: "center" }}>
                    {statusBadge(b.bureauStatus)}
                    {owned && editingBureau !== b.id && (
                      <button onClick={() => startEdit(b)} style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "3px 10px", fontSize: 11.5, color: theme.color.muted, cursor: "pointer" }}>Edit</button>
                    )}
                    <button onClick={() => unlinkBureau(b)} style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "3px 10px", fontSize: 11.5, color: theme.color.dangerText, cursor: "pointer" }}>Unlink</button>
                  </div>
                </div>
                {editingBureau === b.id && (
                  <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
                    <label style={{ fontSize: 12, color: theme.color.muted }}>Country<input value={edit.country} onChange={(e) => setEdit({ ...edit, country: e.target.value })} style={fieldStyle} /></label>
                    <label style={{ fontSize: 12, color: theme.color.muted }}>Contact email<input type="email" value={edit.contactEmail} onChange={(e) => setEdit({ ...edit, contactEmail: e.target.value })} style={fieldStyle} /></label>
                    <label style={{ fontSize: 12, color: theme.color.muted }}>Website<input type="url" value={edit.website} onChange={(e) => setEdit({ ...edit, website: e.target.value })} placeholder="https://" style={fieldStyle} /></label>
                    <label style={{ fontSize: 12, color: theme.color.muted, gridColumn: "1 / -1" }}>Description<textarea rows={2} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} style={{ ...fieldStyle, resize: "vertical" }} /></label>
                    <div style={{ display: "flex", gap: 8, gridColumn: "1 / -1" }}>
                      <button onClick={() => saveBureau(b.id)} style={{ ...btnPrimary, padding: "6px 14px", fontSize: 12.5 }}>Save</button>
                      <button onClick={() => setEditingBureau(null)} style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "6px 14px", fontSize: 12.5, color: theme.color.muted, cursor: "pointer" }}>Cancel</button>
                      <span style={{ fontSize: 11.5, color: theme.color.faint, alignSelf: "center" }}>The name and accreditation were reviewed by the platform team and cannot be changed here.</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!showCreateForm ? (
        <button
          onClick={() => setShowCreateForm(true)}
          style={{
            padding: "8px 16px", background: theme.color.accent, color: "#fff",
            border: "none", borderRadius: theme.radius.md, cursor: "pointer", fontSize: 13,
            marginBottom: 32,
          }}
        >
          Register an Auditor Bureau
        </button>
      ) : (
        <form onSubmit={handleCreate} style={{ ...card, padding: 20, marginBottom: 32, maxWidth: 560 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, marginBottom: 16 }}>
            Register a new Auditor Bureau
          </div>

          {[
            { label: "Bureau name *", key: "name", placeholder: "e.g. Acme Certification Ltd", type: "text" },
            { label: "Country", key: "country", placeholder: "e.g. United Kingdom", type: "text" },
            { label: "Contact email", key: "contactEmail", placeholder: "audits@yourfirm.com", type: "email" },
            { label: "Website", key: "website", placeholder: "https://", type: "url" },
            { label: "Accreditation ref / cert number", key: "accreditation", placeholder: "e.g. UKAS cert no. 1234", type: "text" },
          ].map(({ label, key, placeholder, type }) => (
            <div key={key} style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 4 }}>{label}</label>
              <input
                type={type}
                value={form[key as keyof typeof form]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                placeholder={placeholder}
                style={{
                  width: "100%", boxSizing: "border-box", padding: "8px 10px",
                  border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.sm,
                  background: theme.color.bg, color: theme.color.text, fontSize: 13,
                }}
              />
            </div>
          ))}

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 4 }}>
              Accreditation body *
            </label>
            <select
              value={form.accreditationBody}
              onChange={e => setForm(f => ({ ...f, accreditationBody: e.target.value }))}
              style={{
                width: "100%", padding: "8px 10px",
                border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.sm,
                background: theme.color.bg, color: theme.color.text, fontSize: 13,
              }}
            >
              <option value="">Select…</option>
              {ACCREDITATION_BODIES.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 4 }}>Description (optional)</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3}
              placeholder="Brief description of your bureau's scope and specialisms"
              style={{
                width: "100%", boxSizing: "border-box", padding: "8px 10px",
                border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.sm,
                background: theme.color.bg, color: theme.color.text, fontSize: 13,
                resize: "vertical",
              }}
            />
          </div>

          {formError && <div style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 10 }}>{formError}</div>}

          <div style={{ display: "flex", gap: 10 }}>
            <button type="submit" disabled={submitting} style={{
              padding: "8px 16px", background: theme.color.accent, color: "#fff",
              border: "none", borderRadius: theme.radius.md, cursor: "pointer", fontSize: 13,
            }}>
              {submitting ? "Submitting…" : "Submit for Review"}
            </button>
            <button type="button" onClick={() => setShowCreateForm(false)} style={{
              padding: "8px 16px", background: "transparent", color: theme.color.muted,
              border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.md, cursor: "pointer", fontSize: 13,
            }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Staff access — org-level, automatic once bureau is active */}
      {hasActiveBureau && (
        <div style={{ ...card, padding: "12px 16px", fontSize: 13, color: theme.color.muted, lineHeight: 1.6 }}>
          <CheckCircle2 size={14} style={{ verticalAlign: "-2px", marginRight: 6, color: theme.color.success }} /><strong style={{ color: theme.color.heading }}>Auditor Portal active.</strong>{" "}
          All consultants in your organisation can now sign in and choose the Auditor Portal from the role picker.
          No per-user setup is required.
        </div>
      )}

      {!hasActiveBureau && bureaus.length > 0 && (
        <div style={{ fontSize: 13, color: theme.color.muted, fontStyle: "italic" }}>
          Auditor Portal access becomes available to all staff once your bureau has been approved by the platform team.
        </div>
      )}
    </div>
  );
}

// ── Invites tab ──────────────────────────────────────────────────────────

function InvitesTab() {
  const [invites, setInvites] = useState<InviteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  function load() {
    setLoading(true);
    api.get<{ invites: InviteSummary[] }>("/invites")
      .then((d) => setInvites(d.invites))
      .catch(() => setError("Failed to load invites."))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  if (loading) return <LoadingBlock label="Loading invitations…" />;
  if (error)   return <ErrorBlock message={error} onRetry={load} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Invitations sent</div>
        <div style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 16 }}>
          Clients and staff you have invited. Pending and expired invites can be resent with a fresh link.
        </div>
        <InviteHistoryTable invites={invites} onResent={load} onRevoke={revokeInvite} />
      </div>
    </div>
  );
}

// The Today page lives in pages/consultant/TodayPage.tsx.
// ── Dashboard tab ─────────────────────────────────────────────────────────

function DashboardTab({ orgs, onSelect, onOpenActions }: { orgs: OrgSummary[]; onSelect: (id: string, name: string) => void; onOpenActions: () => void }) {
  const [events, setEvents]   = useState<ConsultantEvent[] | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);

  useEffect(() => {
    api.get<{ events: ConsultantEvent[] }>("/consultant/schedule", { from: new Date().toISOString().slice(0, 10), to: new Date(Date.now() + 14 * 86_400_000).toISOString().slice(0, 10) })
      .then((d) => setEvents(d.events)).catch(() => setEvents([]));
    api.get<{ activity: ActivityEntry[] }>("/consultant/activity", { limit: "6" })
      .then((d) => setActivity(d.activity.slice(0, 6))).catch(() => {});
  }, []);

  const total        = orgs.length;
  const strong       = orgs.filter((o) => scoreBand(o.latestScore) === "strong").length;
  const compliant    = orgs.filter((o) => scoreBand(o.latestScore) === "compliant").length;
  const partial      = orgs.filter((o) => scoreBand(o.latestScore) === "partial").length;
  const nonCompliant = orgs.filter((o) => scoreBand(o.latestScore) === "non_compliant").length;
  const unassessed   = orgs.filter((o) => scoreBand(o.latestScore) === "unassessed").length;

  const now14 = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
  const upcoming = (events ?? [])
    .filter((e) => !e.completedAt && new Date(e.eventDate) >= new Date() && new Date(e.eventDate) <= now14)
    .sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime())
    .slice(0, 6);

  // The Needs Action pill and the sidebar badge's rule (lib/portfolioStatus.ts). A low score is
  // its own band in Portfolio Health above, not an action.
  const needsAttention = orgs.filter(clientNeedsAction);
  const noFrameworks = orgs.filter((o) => o.frameworks.length === 0);
  const noUsers      = orgs.filter((o) => o.userCount === 0 && o.frameworks.length > 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Actions tile */}
      <ActionsTile endpoint="/consultant/actions" onClick={onOpenActions} />

      {/* Score distribution */}
      {total > 0 && (
        <div style={{ ...card }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 12 }}>Portfolio Health</div>
          <div style={{ display: "flex", height: 14, borderRadius: 3, overflow: "hidden", marginBottom: 10, gap: 2 }}>
            {strong       > 0 && <div style={{ flex: strong,       background: theme.color.success, borderRadius: 3 }} title={`${SCORE_BAND_LABELS.strong}: ${strong}`} />}
            {compliant    > 0 && <div style={{ flex: compliant,    background: theme.color.successText, borderRadius: 3 }} title={`Compliant (75–79%): ${compliant}`} />}
            {partial      > 0 && <div style={{ flex: partial,      background: theme.color.warning, borderRadius: 3 }} title={`Partial: ${partial}`} />}
            {nonCompliant > 0 && <div style={{ flex: nonCompliant, background: theme.color.danger, borderRadius: 3 }} title={`Non-Compliant: ${nonCompliant}`} />}
            {unassessed   > 0 && <div style={{ flex: unassessed,   background: theme.color.border, borderRadius: 3 }} title={`Not assessed: ${unassessed}`} />}
          </div>
          <div style={{ display: "flex", gap: 20, fontSize: 12, flexWrap: "wrap" }}>
            {[
              { count: strong,       label: SCORE_BAND_LABELS.strong, color: theme.color.success },
              { count: compliant,    label: "Compliant (75–79%)", color: theme.color.successText },
              { count: partial,      label: "Partial",         color: theme.color.warningText },
              { count: nonCompliant, label: "Non-Compliant",   color: theme.color.dangerText },
              { count: unassessed,   label: "Not Assessed",    color: theme.color.faint },
            ].filter((x) => x.count > 0).map(({ count, label, color }) => (
              <span key={label} style={{ color: theme.color.subtle }}>
                <span style={{ fontWeight: 700, color }}>{count}</span> {label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Two-column: Attention + Upcoming */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>

        {/* Attention */}
        <div style={{ ...card }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>Needs Attention</span>
            {needsAttention.length > 0 && (
              <span style={countTag}>
                {needsAttention.length}
              </span>
            )}
          </div>
          {needsAttention.length === 0 ? (
            <div style={{ fontSize: 12, color: theme.color.successText, fontWeight: 600 }}>All clients on track</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {needsAttention.slice(0, 6).map((o) => (
                <button key={o.id} onClick={() => onSelect(o.id, o.name)} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`,
                  borderRadius: 3, padding: "7px 10px", fontSize: 12,
                  cursor: "pointer", textAlign: "left",
                }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = theme.color.accentBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = theme.color.panelAlt)}
                >
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: scoreColor(o.latestScore), flexShrink: 0 }} />
                  <span style={{ flex: 1, fontWeight: 600, color: theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {o.name}
                  </span>
                  <span style={{ fontSize: 11, color: scoreColor(o.latestScore), fontWeight: 700, flexShrink: 0 }}>
                    {o.latestScore !== null ? `${o.latestScore}%` : "—"}
                  </span>
                </button>
              ))}
              {needsAttention.length > 6 && (
                <div style={{ fontSize: 11, color: theme.color.faint, textAlign: "center" }}>+{needsAttention.length - 6} more</div>
              )}
            </div>
          )}
        </div>

        {/* Upcoming events */}
        <div style={{ ...card }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 12 }}>Upcoming (14 days)</div>
          {events === null ? (
            <div style={{ fontSize: 12, color: theme.color.faint }}>Loading…</div>
          ) : upcoming.length === 0 ? (
            <div style={{ fontSize: 12, color: theme.color.faint }}>No events in the next 14 days</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {upcoming.map((e) => {
                const kc = KIND_STYLE[e.kind] ?? { bg: theme.color.panelAlt, color: theme.color.muted };
                const dateStr = new Date(e.eventDate).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
                return (
                  <button key={e.id}
                    onClick={() => e.organizationId && e.organizationName && onSelect(e.organizationId, e.organizationName)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`,
                      borderRadius: 3, padding: "7px 10px", cursor: "pointer", textAlign: "left",
                    }}
                    onMouseEnter={(ev) => (ev.currentTarget.style.background = theme.color.accentBg)}
                    onMouseLeave={(ev) => (ev.currentTarget.style.background = theme.color.panelAlt)}
                  >
                    <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 3, background: kc.bg, color: kc.color, fontWeight: 700, flexShrink: 0, textTransform: "capitalize" }}>{e.kind}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {e.organizationName}
                      </div>
                      <div style={{ fontSize: 10, color: theme.color.subtle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.title}</div>
                    </div>
                    <span style={{ fontSize: 10, color: theme.color.faint, flexShrink: 0 }}>{dateStr}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Onboarding issues */}
      {(noFrameworks.length > 0 || noUsers.length > 0) && (
        <div style={{ ...card, borderLeft: `4px solid ${theme.color.warning}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.warningText, marginBottom: 12 }}>
            Onboarding required ({noFrameworks.length + noUsers.length})
          </div>
          {noFrameworks.length > 0 && (
            <div style={{ marginBottom: noUsers.length > 0 ? 12 : 0 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.subtle, marginBottom: 6 }}>No frameworks enrolled</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {noFrameworks.map((o) => (
                  <button key={o.id} onClick={() => onSelect(o.id, o.name)} style={{
                    fontSize: 11, padding: "4px 10px", borderRadius: 3, cursor: "pointer",
                    background: theme.color.warningBg, border: `1px solid ${theme.color.warning}44`,
                    color: theme.color.warningText, fontWeight: 600,
                  }}>{o.name}</button>
                ))}
              </div>
            </div>
          )}
          {noUsers.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.subtle, marginBottom: 6 }}>No client users yet</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {noUsers.map((o) => (
                  <button key={o.id} onClick={() => onSelect(o.id, o.name)} style={{
                    fontSize: 11, padding: "4px 10px", borderRadius: 3, cursor: "pointer",
                    background: theme.color.panel, border: `1px solid ${theme.color.border}`,
                    color: theme.color.subtle, fontWeight: 600,
                  }}>{o.name}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recent activity */}
      {activity.length > 0 && (
        <div style={{ ...card }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 12 }}>Recent Activity</div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {activity.map((entry, i) => {
              const color  = categoryColor(entry.category);
              const isLast = i === activity.length - 1;
              return (
                <div key={entry.id} style={{
                  display: "flex", gap: 12, padding: "10px 0", alignItems: "flex-start",
                  borderBottom: isLast ? "none" : `1px solid ${theme.color.border}`,
                }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, marginTop: 3, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {auditActionLabel(entry.action)}
                    </div>
                    {entry.organizationName && entry.organizationId && (
                      <button onClick={() => onSelect(entry.organizationId!, entry.organizationName!)} style={{
                        background: "none", border: "none", padding: 0, fontSize: 11,
                        color: theme.color.accentText, cursor: "pointer", fontWeight: 600,
                      }}>{entry.organizationName}</button>
                    )}
                  </div>
                  <span style={{ fontSize: 11, color: theme.color.faint, flexShrink: 0 }}>{timeAgo(entry.createdAt)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────

function EmptyPortfolio({ onInvite }: { onInvite: () => void }) {
  const steps = [
    {
      n: "1",
      title: "Invite a client",
      body: "Send an invite to your client's contact email. They'll receive a link to create their account.",
    },
    {
      n: "2",
      title: "They set up their organisation",
      body: "The client creates a password, logs in, and completes their organisation profile and ISMS scope.",
    },
    {
      n: "3",
      title: "Track compliance progress",
      body: "Review their Statement of Applicability, run AI compliance checks, and manage their certification journey.",
    },
  ];

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 32px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: 56, height: 56, borderRadius: 3,
          background: theme.color.accentBg,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 26, marginBottom: 20,
        }}
      >
        <Building2 size={26} color="var(--c-accentText)" />
      </div>

      <div style={{ fontSize: 22, fontWeight: 800, color: theme.color.heading, marginBottom: 8 }}>
        No clients yet
      </div>
      <div style={{ fontSize: 14, color: theme.color.subtle, maxWidth: 420, lineHeight: 1.6, marginBottom: 36 }}>
        Invite your first client organisation to get started. Once they've joined, you'll be able to track their ISO 27001 compliance journey from here.
      </div>

      {/* Steps */}
      <div
        style={{
          display: "flex", gap: 0, maxWidth: 680, width: "100%",
          marginBottom: 36, position: "relative",
        }}
      >
        {/* Connector line */}
        <div
          style={{
            position: "absolute", top: 20, left: "calc(16.6% + 12px)",
            right: "calc(16.6% + 12px)", height: 1,
            background: theme.color.border, zIndex: 0,
          }}
        />

        {steps.map((step) => (
          <div
            key={step.n}
            style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "0 16px", position: "relative", zIndex: 1 }}
          >
            <div
              style={{
                width: 40, height: 40, borderRadius: "50%",
                background: theme.color.accentBg,
                border: `2px solid ${theme.color.accent}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 14, fontWeight: 800, color: theme.color.accentText,
              }}
            >
              {step.n}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>{step.title}</div>
            <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>{step.body}</div>
          </div>
        ))}
      </div>

      <button onClick={onInvite} style={{ ...btnPrimary, fontSize: 14, padding: "12px 28px" }}>
        + Invite your first client
      </button>
    </div>
  );
}

// ── HardeningTab ─────────────────────────────────────────────────────────

interface HardeningOrgSummary {
  id: string;
  name: string;
  intuneConnected: boolean;
  benchmarks: Array<{
    id: string;
    name: string;
    version: string;
    category: string;
    controlCount: number;
    statusCounts: Record<string, number>;
  }>;
}

const CAT_LABELS: Record<string, string> = {
  cloud:            "M365 & Azure",
  endpoint:         "Endpoint",
  intune:           "Intune",
  "windows-server": "Win Server",
  "windows-desktop":"Win Desktop",
};

function HardeningProgressBar({ controlCount, statusCounts }: { controlCount: number; statusCounts: Record<string, number> }) {
  const compliant    = statusCounts.compliant ?? 0;
  const nonCompliant = statusCounts.non_compliant ?? 0;
  const inProgress   = statusCounts.in_progress ?? 0;
  const pct = controlCount > 0 ? Math.round((compliant / controlCount) * 100) : 0;
  return (
    <div>
      <div style={{ fontSize: 11, color: theme.color.faint, marginBottom: 3 }}>{pct}% compliant</div>
      <div style={{ height: 4, borderRadius: 3, background: theme.color.panelAlt, overflow: "hidden", display: "flex" }}>
        <div style={{ width: `${pct}%`, background: theme.color.success }} />
        <div style={{ width: `${controlCount > 0 ? Math.round((nonCompliant / controlCount) * 100) : 0}%`, background: theme.color.danger }} />
        <div style={{ width: `${controlCount > 0 ? Math.round((inProgress / controlCount) * 100) : 0}%`, background: theme.color.warning }} />
      </div>
    </div>
  );
}

function HardeningTab({ onSelect }: { onSelect: (id: string, name: string) => void }) {
  const [orgs, setOrgs] = useState<HardeningOrgSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ orgs: HardeningOrgSummary[] }>("/hardening/portfolio")
      .then((d) => setOrgs(d.orgs))
      .catch((e) => setLoadError(e instanceof Error && e.message ? e.message : "Could not load the hardening portfolio"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingBlock label="Loading hardening portfolio…" />;
  if (loadError) return <ErrorBlock message={loadError} />;

  const withSubs  = orgs.filter((o) => o.benchmarks.length > 0);
  const withNone  = orgs.filter((o) => o.benchmarks.length === 0);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: theme.color.heading }}>CIS Hardening</div>
        <div style={{ fontSize: 13, color: theme.color.subtle, marginTop: 4 }}>
          CIS Microsoft benchmark subscriptions and progress across your portfolio.
        </div>
      </div>

      {withSubs.length === 0 && (
        <div style={{ ...emptyFrame, color: theme.color.subtle, fontSize: 13 }}>
          No clients have CIS hardening benchmarks enabled yet. Use My Services on each client to subscribe them to benchmarks ({gbpMo(HARDENING_BENCHMARK_PENCE)} each, Self-Service + AI tier and above).
        </div>
      )}

      {withSubs.map((org) => {
        const isOpen = expanded === org.id;
        const totalControls = org.benchmarks.reduce((s, b) => s + b.controlCount, 0);
        const totalCompliant = org.benchmarks.reduce((s, b) => s + (b.statusCounts.compliant ?? 0), 0);
        const pct = totalControls > 0 ? Math.round((totalCompliant / totalControls) * 100) : null;

        return (
          <div key={org.id} style={{ ...card, marginBottom: 10, padding: 0, overflow: "hidden" }}>
            {/* Header */}
            <div
              onClick={() => setExpanded(isOpen ? null : org.id)}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", cursor: "pointer" }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading }}>{org.name}</span>
                  {org.intuneConnected && (
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 3, background: theme.color.accentBg, color: theme.color.accentText }}>
                      Intune
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 2 }}>
                  {org.benchmarks.length} benchmark{org.benchmarks.length !== 1 ? "s" : ""}
                </div>
              </div>
              {pct !== null && (
                <div style={{ fontSize: 13, fontWeight: 700, color: pct >= 80 ? theme.color.successText : pct >= 50 ? theme.color.warningText : theme.color.dangerText }}>
                  {pct}%
                </div>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); onSelect(org.id, org.name); }}
                style={{ fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.muted, cursor: "pointer" }}
              >
                Open Client →
              </button>
              <div style={{ color: theme.color.faint, fontSize: 10 }}>{isOpen ? "▲" : "▼"}</div>
            </div>

            {isOpen && (
              <div style={{ borderTop: `1px solid ${theme.color.border}`, padding: "12px 16px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
                  {org.benchmarks.map((b) => (
                    <div key={b.id} style={{ background: theme.color.panelAlt, borderRadius: 3, padding: "10px 12px" }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 2 }}>{b.name}</div>
                      <div style={{ fontSize: 10, color: theme.color.faint, marginBottom: 6 }}>
                        {CAT_LABELS[b.category] ?? b.category} · {b.version}
                      </div>
                      {b.controlCount > 0 ? (
                        <HardeningProgressBar controlCount={b.controlCount} statusCounts={b.statusCounts} />
                      ) : (
                        <div style={{ fontSize: 10, color: theme.color.faint }}>Controls not yet loaded</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {withNone.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: theme.color.faint, marginBottom: 8 }}>
            No benchmarks subscribed
          </div>
          {withNone.map((org) => (
            <div key={org.id} style={{ ...card, marginBottom: 6, display: "flex", alignItems: "center", gap: 12, padding: "10px 16px" }}>
              <span style={{ flex: 1, fontSize: 13, color: theme.color.subtle }}>{org.name}</span>
              <button
                onClick={() => onSelect(org.id, org.name)}
                style={{ fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.muted, cursor: "pointer" }}
              >
                Open Client →
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── SelfOrgSetupModal ─────────────────────────────────────────────────────

const SETUP_FW_ORDER = [
  "iso27001", "iso9001", "iso42001", "soc2", "pci_dss",
  "cyber_essentials", "cyber_essentials_plus", "nist_csf", "gdpr", "uk_gdpr", "iso45001", "ncsc_caf",
] as const;

const SETUP_FW_LABELS: Record<string, string> = {
  iso27001: "ISO 27001:2022", iso9001: "ISO 9001:2015", iso42001: "ISO 42001:2023",
  soc2: "SOC 2", pci_dss: "PCI DSS v4.0",
  cyber_essentials: "Cyber Essentials", cyber_essentials_plus: "Cyber Essentials Plus",
  nist_csf: "NIST CSF 2.0", gdpr: "GDPR", uk_gdpr: "UK GDPR", iso45001: "ISO 45001", ncsc_caf: "NCSC CAF",
};

const overlayStyle: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
};

function SelfOrgSetupModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (orgId: string, orgName: string) => void;
}) {
  const [orgName, setOrgName] = useState("");
  const [selectedFws, setSelectedFws] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  function toggleFw(id: string) {
    setSelectedFws((prev) => {
      // Deselecting the last paid standard takes the free reference frameworks (NIST CSF) with it.
      if (prev.includes(id)) return withoutOrphanedReferenceFrameworks(prev.filter((x) => x !== id));
      if (id === "cyber_essentials")      return [...prev.filter((x) => x !== "cyber_essentials_plus"), id];
      if (id === "cyber_essentials_plus") return [...prev.filter((x) => x !== "cyber_essentials"),      id];
      return [...prev, id];
    });
  }
  const paidSelected = hasPaidStandard(selectedFws);

  async function handleSubmit() {
    if (!orgName.trim()) return;
    setError(""); setSubmitting(true);
    try {
      const result = await api.post<{ ok: boolean; org: { id: string; name: string } }>(
        "/consultant/self-org/setup",
        { orgName: orgName.trim(), frameworkIds: selectedFws },
      );
      onCreated(result.org.id, result.org.name);
    } catch (err: unknown) {
      const e = err as { payload?: { error?: string }; message?: string };
      setError(apiErrorMessage(e, e?.message || "Failed to set up your organisation"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...floatingSheet, padding: 28, width: "100%", maxWidth: 500 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, paddingBottom: 12, borderBottom: "1px solid var(--c-line)" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>Set up your company's compliance</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, fontSize: 20, lineHeight: 1 }}>×</button>
        </div>

        <p style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 20, lineHeight: 1.5 }}>
          This creates your firm as a client in your own portfolio so you can manage your own compliance alongside that of your clients. You'll reach it from <strong>Switch workspace</strong> in the sidebar and the account menu.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 16 }}>
          <label style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 600 }}>Organisation name</label>
          <input
            style={{
              background: theme.color.bg, border: `1px solid ${theme.color.border}`,
              borderRadius: 3, padding: "9px 12px", fontSize: 13,
              color: theme.color.text, outline: "none", width: "100%", boxSizing: "border-box" as const,
            }}
            value={orgName}
            autoFocus
            placeholder="Company Name"
            onChange={(e) => setOrgName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !submitting && orgName.trim() && handleSubmit()}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 20 }}>
          <label style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 600 }}>Standards (optional — you can add more later)</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
            {SETUP_FW_ORDER.map((id) => {
              const active  = selectedFws.includes(id);
              const needsStandard = FREE_REFERENCE_FRAMEWORKS.has(id as FrameworkId) && !paidSelected;
              const blocked = needsStandard
                           || (id === "cyber_essentials"      && selectedFws.includes("cyber_essentials_plus"))
                           || (id === "cyber_essentials_plus" && selectedFws.includes("cyber_essentials"));
              return (
                <button
                  key={id}
                  onClick={() => !blocked && toggleFw(id)}
                  disabled={blocked}
                  title={needsStandard ? "NIST CSF is free alongside any standard — select a standard first" : blocked ? "CE and CE+ are mutually exclusive" : undefined}
                  style={{
                    fontSize: 11, padding: "4px 10px", borderRadius: 3,
                    cursor: blocked ? "not-allowed" : "pointer",
                    fontWeight: active ? 700 : 400,
                    border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
                    background: active ? theme.color.accentBg : "transparent",
                    color: active ? theme.color.accentText : theme.color.muted,
                    opacity: blocked ? 0.4 : 1,
                  }}
                >
                  {SETUP_FW_LABELS[id]}
                </button>
              );
            })}
          </div>
        </div>

        {error && <div style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 12 }}>{error}</div>}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{
              padding: "8px 18px", borderRadius: 3, border: `1px solid ${theme.color.border}`,
              background: "transparent", color: theme.color.muted, fontSize: 13, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !orgName.trim()}
            style={{
              padding: "8px 20px", borderRadius: 3, border: "none",
              background: theme.color.accent, color: "#fff", fontSize: 13, fontWeight: 600,
              cursor: submitting || !orgName.trim() ? "default" : "pointer",
              opacity: submitting || !orgName.trim() ? 0.6 : 1,
            }}
          >
            {submitting ? "Creating…" : "Set up my company"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Workspace pages ───────────────────────────────────────────────────────
// Each route renders one of the components above inside the page column.
// Pages that need the portfolio (clients, billing, reports, dashboard) share
// the loading / error / empty handling and the KPI tiles.

/** Routes built on the portfolio: they show the KPI tiles and the empty state when there are no clients. */
const PORTFOLIO_TABS: ReadonlySet<ConsultantTab> = new Set<ConsultantTab>(["dashboard", "clients", "billing", "reports"]);

function PortfolioTiles() {
  const { portfolio } = useConsultantShell();
  const summary = portfolio.summary;
  if (!summary || portfolio.orgs.length === 0) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 16, marginBottom: 28 }}>
      <StatTile label="Clients Managed" value={String(summary.total)} sub="excluding your own organisation" />
      <StatTile
        label="Portfolio Avg Score"
        value={summary.avgScore !== null ? `${summary.avgScore}%` : "n/a"}
        sub="SOA score, across all clients"
        color={summary.avgScore !== null ? scoreColor(summary.avgScore) : theme.color.faint}
      />
      <StatTile
        label={SCORE_BAND_LABELS.strong}
        value={String(summary.strongCount)}
        sub="SOA score, not a certification"
        color={summary.strongCount > 0 ? theme.color.successText : theme.color.subtle}
      />
      <StatTile
        label="Needs Attention"
        value={String(summary.needsAttention)}
        sub="open findings or tier requests"
        color={summary.needsAttention > 0 ? theme.color.dangerText : theme.color.successText}
      />
      {/* Firm revenue is for firm administrators; the API sends null to everyone else. */}
      {summary.totalMonthlyBilledPence !== null && (
        <StatTile
          label="Monthly Billed"
          value={fmtGbp(summary.totalMonthlyBilledPence)}
          sub="all clients"
          color={theme.color.successText}
        />
      )}
      {summary.totalMonthlyConsultancyRevenuePence !== null && (
        <StatTile
          label="Your Revenue"
          value={fmtGbp(summary.totalMonthlyConsultancyRevenuePence)}
          sub="consultancy time"
          color={theme.color.consultant}
        />
      )}
    </div>
  );
}

function WorkspacePage({ tab, children }: { tab: ConsultantTab; children: ReactNode }) {
  const { portfolio, reloadPortfolio, openInviteClient } = useConsultantShell();

  let body: ReactNode = children;
  if (PORTFOLIO_TABS.has(tab)) {
    if (portfolio.loading && portfolio.orgs.length === 0) body = <LoadingBlock label="Loading portfolio" />;
    else if (portfolio.error) body = <ErrorBlock message={portfolio.error} onRetry={reloadPortfolio} />;
    else if (portfolio.orgs.length === 0) body = <EmptyPortfolio onInvite={openInviteClient} />;
  }

  return (
    <div className="page-pad" style={{ boxSizing: "border-box" as const }}>
      {PORTFOLIO_TABS.has(tab) && <PortfolioTiles />}
      {body}
    </div>
  );
}

/** Page-level guard mirroring the API's firm permissions; the sidebar already hides the item. */
function RequirePermission({ permission, children }: { permission: string; children: ReactNode }) {
  const { permissions } = useConsultantShell();
  if (permissions.includes(permission)) return <>{children}</>;
  return (
    <div className="page-pad">
      <div style={{ ...card, color: theme.color.subtle, fontSize: 13, lineHeight: 1.6 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>No access</div>
        This page needs the <code>{permission}</code> permission. Ask your lead consultant if you need it.
      </div>
    </div>
  );
}

function useSelectClient() {
  const navigate = useNavigate();
  return useCallback((id: string) => navigate(clientOverviewPath(id)), [navigate]);
}

function DashboardPage() {
  const navigate = useNavigate();
  const { portfolio } = useConsultantShell();
  const select = useSelectClient();
  return (
    <WorkspacePage tab="dashboard">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}><ScopeToggle compact /></div>
      <PipelinePanel onOpen={select} />
      <DashboardTab orgs={portfolio.orgs} onSelect={select} onOpenActions={() => navigate("/consultant/actions")} />
    </WorkspacePage>
  );
}

function ClientsPage() {
  const shell = useConsultantShell();
  const select = useSelectClient();

  async function handleSelfOrgChange(orgId: string | null) {
    try {
      await api.put<{ ok: boolean; selfOrgId: string | null }>("/consultant/self-org", { organizationId: orgId });
      shell.setSelfOrgId(orgId);
    } catch {
      shell.notify("Could not update which organisation is your company. Please try again.");
    }
  }

  async function handleRemoveClient(orgId: string) {
    try {
      await api.del(`/consultant/clients/${orgId}`);
      shell.forgetClient(orgId);
    } catch {
      shell.notify("Could not remove the client. Please try again.");
    }
  }

  // Table (sortable, filterable, bulk actions) or the original cards. A link that
  // carries a filter (Today's "set the sector for N clients") always opens the table.
  const [searchParams] = useSearchParams();
  const [view, setViewState] = useState<"table" | "cards">(() => {
    try { return window.localStorage.getItem("consultant.clients.view") === "cards" ? "cards" : "table"; } catch { return "table"; }
  });
  const setView = (v: "table" | "cards") => {
    setViewState(v);
    try { window.localStorage.setItem("consultant.clients.view", v); } catch { /* preference only */ }
  };
  const effectiveView = searchParams.get("filter") ? "table" : view;
  const viewPill = (v: "table" | "cards", label: string) => (
    <button onClick={() => setView(v)} aria-pressed={effectiveView === v}
      style={{ padding: "5px 12px", borderRadius: 3, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${effectiveView === v ? theme.color.accent : theme.color.border}`, background: effectiveView === v ? theme.color.accentBg : "transparent", color: effectiveView === v ? theme.color.accentText : theme.color.subtle }}>
      {label}
    </button>
  );

  return (
    <WorkspacePage tab="clients">
      <PortfolioRiskTiles onSelect={select} />
      <AttentionPanel orgs={shell.portfolio.orgs} onSelect={select} />
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginBottom: 12 }}>
        {viewPill("table", "Table")}
        {viewPill("cards", "Cards")}
      </div>
      {effectiveView === "table" ? (
        <ClientsTable orgs={shell.portfolio.orgs} onOpen={select} selfOrgId={shell.portfolio.selfOrgId} />
      ) : (
        <PortfolioTab
          orgs={shell.portfolio.orgs}
          onSelect={select}
          onInvite={shell.openInviteClient}
          onInviteStaff={shell.openInviteStaff}
          onSetupSelfOrg={shell.openSelfOrgSetup}
          selfOrgId={shell.portfolio.selfOrgId}
          onSelfOrgChange={handleSelfOrgChange}
          onRemove={handleRemoveClient}
        />
      )}
    </WorkspacePage>
  );
}

function BillingPage() {
  const { portfolio } = useConsultantShell();
  const select = useSelectClient();
  return <WorkspacePage tab="billing"><BillingTab orgs={portfolio.orgs} onSelect={select} /></WorkspacePage>;
}

function ReportsPage() {
  const { portfolio } = useConsultantShell();
  const select = useSelectClient();
  return <WorkspacePage tab="reports"><ReportsTab orgs={portfolio.orgs} onSelect={select} /></WorkspacePage>;
}

// Organisation groups of this consultancy (pages/ConsultantGroups.tsx).
// Creating, editing and dissolving needs org:admin (senior consultant and up),
// mirroring what the API enforces.
function GroupsPage() {
  const { portfolio, permissions } = useConsultantShell();
  const select = useSelectClient();
  return (
    <WorkspacePage tab="groups">
      <ConsultantGroupsTab
        clients={portfolio.orgs.map((o) => ({ id: o.id, name: o.name }))}
        canManage={permissions.includes("org:admin")}
        onOpenClient={(id) => select(id)}
      />
    </WorkspacePage>
  );
}

function DirectorySyncSettingsPage() {
  return (
    <div className="page-pad">
      <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>Directory Sync</h1>
      <p style={{ margin: "0 0 20px", fontSize: 13, color: theme.color.subtle }}>
        Give your consultancy staff their consultant role from your Active Directory / Entra ID groups.
      </p>
      <DirectorySyncPanel scope="firm" />
    </div>
  );
}

function SignInSettingsPage() {
  return (
    <div className="page-pad">
      <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>Sign-in</h1>
      <p style={{ margin: "0 0 20px", fontSize: 13, color: theme.color.subtle }}>
        How your consultancy's staff sign in. Your clients set their own policy; this one applies to your staff wherever they work.
      </p>
      <SignInPolicyPanel />
    </div>
  );
}

// ── ConsultantWorkspace: AppShell + nested routes under /consultant/* ─────

function ConsultantWorkspace() {
  // A consultant session outside the read-through never scopes API calls to a
  // client (the read-through sets the id during its own render, see
  // pages/consultant/ConsultantClientPortal.tsx).
  setViewingOrgId(null);

  const shell = useConsultantShell();
  const { branding } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const switches = useConsultantSwitches();
  const { user, permissions, selfOrg, recentClients, clients, portfolio } = shell;

  const clientMatch = matchPath("/consultant/clients/:orgId/*", location.pathname);
  const selectedId = clientMatch?.params.orgId ?? null;
  const selectedClient = useMemo<ConsultantNavClient | null>(() => {
    if (!selectedId) return null;
    const lite = clients.find((c) => c.id === selectedId);
    if (lite) return { id: lite.id, name: lite.name, tier: lite.tier, frameworks: lite.frameworks, latestScore: lite.latestScore };
    const org = portfolio.orgs.find((o) => o.id === selectedId);
    if (org) return { id: org.id, name: org.name, tier: org.serviceTier.name, frameworks: org.frameworks.map((f) => f.frameworkId), latestScore: org.latestScore };
    return { id: selectedId, name: "Client" };
  }, [selectedId, clients, portfolio.orgs]);

  const { enterOwnOrgPortal, openAuditorPortal, openSelfOrgSetup } = shell;
  const onOpenOwnOrg = useCallback(() => {
    if (selfOrg) void enterOwnOrgPortal();
    else openSelfOrgSetup();
  }, [selfOrg, enterOwnOrgPortal, openSelfOrgSetup]);
  const onOpenAuditorPortal = useCallback(() => { void openAuditorPortal(); }, [openAuditorPortal]);

  const firmName = branding?.name ?? "Abacas";
  const firmLogoUrl = branding?.logoUrl ?? null;
  const model = useMemo<NavModel>(() => buildConsultantNav({
    user,
    permissions,
    firmName,
    firmLogoUrl,
    selfOrg,
    canActAsAuditor: user.canActAsAuditor,
    recentClients,
    selectedClient,
    onOpenOwnOrg,
    onOpenAuditorPortal,
  }), [user, permissions, firmName, firmLogoUrl, selfOrg, recentClients, selectedClient, onOpenOwnOrg, onOpenAuditorPortal]);

  const adapter = useRouterLinkAdapter();

  // Ctrl/Cmd+K also jumps to any client (fresh light list on every open).
  const paletteExtras = useCallback(async (): Promise<PaletteEntry[]> => {
    const list = await api.get<ClientLite[]>("/consultant/clients").catch(() => [] as ClientLite[]);
    return list.map((c) => ({
      id: `client:${c.id}`,
      label: c.name,
      hint: "Client",
      keywords: [...c.frameworks.map(frameworkLabel), c.sector ?? "", c.tier].filter(Boolean),
      to: clientOverviewPath(c.id),
    }));
  }, []);

  const breadcrumb = selectedClient ? (
    <>
      <Breadcrumb items={[{ label: "Clients", to: "/consultant/clients" }, { label: selectedClient.name }]} />
      <ClientSwitcher orgId={selectedClient.id} currentName={selectedClient.name} mode="overview" />
    </>
  ) : undefined;

  return (
    <AppShell
      model={model}
      adapter={adapter}
      user={user}
      topBar={{ breadcrumb, bell: null }}
      userMenu={{ switches, showExperienceMode: false, versionTo: "/consultant/whats-new" }}
      paletteExtras={paletteExtras}
    >
      <Routes>
        {/* Home is Today: one ranked list of what needs the consultant (docs/NAVIGATION.md section 5). */}
        <Route index element={<Navigate to="actions" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/:orgId" element={<ConsultantClientRoute />} />
        <Route path="calendar" element={<WorkspacePage tab="calendar"><CalendarTab onSelect={(id) => navigate(clientOverviewPath(id))} /></WorkspacePage>} />
        <Route path="findings" element={<WorkspacePage tab="findings"><FindingsPage /></WorkspacePage>} />
        <Route path="actions" element={<WorkspacePage tab="actions"><TodayPage /></WorkspacePage>} />
        <Route path="activity" element={<WorkspacePage tab="activity"><ActivityPage /></WorkspacePage>} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="billing" element={<RequirePermission permission="firm:admin"><BillingPage /></RequirePermission>} />
        <Route path="utilisation" element={<WorkspacePage tab="utilisation"><UtilisationPage /></WorkspacePage>} />
        <Route path="time" element={<WorkspacePage tab="time"><TimePage /></WorkspacePage>} />
        <Route path="revenue" element={<RequirePermission permission="firm:admin"><WorkspacePage tab="revenue"><RevenuePage /></WorkspacePage></RequirePermission>} />
        <Route path="rates" element={<RequirePermission permission="firm:admin"><ConsultantRatesPage /></RequirePermission>} />
        <Route path="invitations" element={<WorkspacePage tab="invitations"><InvitesTab /></WorkspacePage>} />
        <Route path="hardening" element={<WorkspacePage tab="hardening"><HardeningTab onSelect={(id) => navigate(clientOverviewPath(id))} /></WorkspacePage>} />
        <Route path="groups" element={<GroupsPage />} />
        <Route path="settings" element={<Navigate to={permissions.includes("firm:users") ? "users" : "branding"} replace />} />
        <Route path="settings/users" element={<RequirePermission permission="firm:users"><ConsultantUsersPage /></RequirePermission>} />
        <Route path="settings/sso" element={<RequirePermission permission="firm:admin"><ConsultantSsoPage /></RequirePermission>} />
        <Route path="settings/branding" element={<RequirePermission permission="firm:admin"><ConsultantBrandingPage /></RequirePermission>} />
        <Route path="settings/ai" element={<RequirePermission permission="firm:admin"><ConsultantAiSettingsPage /></RequirePermission>} />
        <Route path="settings/directory-sync" element={<RequirePermission permission="firm:admin"><DirectorySyncSettingsPage /></RequirePermission>} />
        <Route path="settings/sign-in" element={<RequirePermission permission="firm:admin"><SignInSettingsPage /></RequirePermission>} />
        <Route path="settings/accreditation" element={<RequirePermission permission="firm:admin"><AuditorAccreditationTab /></RequirePermission>} />
        <Route path="whats-new" element={<WhatsNewPage />} />
        <Route path="platform" element={user.isPlatformOwner ? <PlatformAdminPage /> : <Navigate to={CONSULTANT_HOME} replace />} />
        <Route path="*" element={<Navigate to={CONSULTANT_HOME} replace />} />
      </Routes>
    </AppShell>
  );
}

// ── Main page: shared state + top-level routes ────────────────────────────

const INITIAL_PORTFOLIO: PortfolioState = { orgs: [], summary: null, selfOrgId: null, loading: true, loaded: false, error: "" };

export function ConsultantPortfolioPage({ user, onSwitchRole, openSelfOrgSetup = false, initialNotice = null }: {
  user: CurrentUser;
  /** Re-resolves the signed-in identity after a role switch (auditor, or own-organisation client). */
  onSwitchRole?: () => Promise<void>;
  /** Open the "set up our company" dialog immediately (sign-in as "Client" found no self-org). */
  openSelfOrgSetup?: boolean;
  /** Something to tell the consultant on arrival (a sign-in-time switch that failed). */
  initialNotice?: string | null;
}) {
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientLite[]>([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [portfolio, setPortfolio] = useState<PortfolioState>(INITIAL_PORTFOLIO);
  // Recent clients are stored as ids under a per-user key and resolved
  // against the session's own lists below, so nothing another account could
  // read is ever persisted and a stale or foreign id never renders.
  const [recentIds, setRecentIds] = useState<string[]>(() => readRecentClientIds(user.id));
  const [showInviteClient, setShowInviteClient] = useState(false);
  const [showInviteStaff, setShowInviteStaff] = useState(false);
  const [showSelfOrgSetup, setShowSelfOrgSetup] = useState(openSelfOrgSetup);
  const [notice, setNotice] = useState<string | null>(initialNotice);

  const reloadClients = useCallback(() => {
    api.get<ClientLite[]>("/consultant/clients")
      .then((list) => setClients(Array.isArray(list) ? list : []))
      .catch(() => { /* the switcher and context block fall back to the portfolio */ })
      .finally(() => setClientsLoaded(true));
  }, []);

  const reloadPortfolio = useCallback(() => {
    setPortfolio((p) => ({ ...p, loading: true, error: "" }));
    api.get<PortfolioResponse>("/consultant/portfolio")
      .then((d) => setPortfolio({ orgs: d.organizations, summary: d.summary, selfOrgId: d.selfOrgId ?? null, loading: false, loaded: true, error: "" }))
      .catch(() => setPortfolio((p) => ({ ...p, loading: false, loaded: true, error: "Failed to load portfolio." })));
  }, []);

  useEffect(() => {
    reloadClients();
    reloadPortfolio();
  }, [reloadClients, reloadPortfolio]);

  const selfOrg = useMemo(() => {
    const id = portfolio.selfOrgId;
    if (!id) return null;
    const name = portfolio.orgs.find((o) => o.id === id)?.name ?? clients.find((c) => c.id === id)?.name ?? "Your company";
    return { id, name };
  }, [portfolio.selfOrgId, portfolio.orgs, clients]);

  const forgetClient = useCallback((orgId: string) => {
    setPortfolio((p) => ({
      ...p,
      orgs: p.orgs.filter((o) => o.id !== orgId),
      // The consultancy's own organisation was never in the client total.
      summary: p.summary && orgId !== p.selfOrgId ? { ...p.summary, total: Math.max(0, p.summary.total - 1) } : p.summary,
      selfOrgId: p.selfOrgId === orgId ? null : p.selfOrgId,
    }));
    setClients((list) => list.filter((c) => c.id !== orgId));
    setRecentIds((list) => forgetRecentClientIds(user.id, list, [orgId]));
  }, [user.id]);

  const setSelfOrgId = useCallback((orgId: string | null) => {
    setPortfolio((p) => ({ ...p, selfOrgId: orgId }));
  }, []);

  // Recent clients and "My clients / Whole firm" follow the consultant between devices
  // (User.consultantPrefs). localStorage stays as the instant first paint; the server
  // copy wins once loaded, and a list only this browser knows is sent up.
  const [scope, setScopeState] = useState<Scope>("firm");
  const recentSynced = useRef(false);
  useEffect(() => {
    let cancelled = false;
    api.get<{ scope: Scope; recentClientIds: string[] }>("/consultant/workspace/prefs")
      .then((p) => {
        if (cancelled) return;
        setScopeState(p.scope);
        if (p.recentClientIds.length > 0) {
          setRecentIds(p.recentClientIds.slice(0, RECENT_CLIENTS_MAX));
        } else {
          const local = readRecentClientIds(user.id);
          if (local.length) void api.patch("/consultant/workspace/prefs", { recentClientIds: local }).catch(() => undefined);
        }
      })
      .catch(() => { /* the local copy and the firm view stand in */ })
      .finally(() => { recentSynced.current = true; });
    return () => { cancelled = true; };
  }, [user.id]);

  const setScope = useCallback((next: Scope) => {
    setScopeState(next);
    api.patch("/consultant/workspace/prefs", { scope: next }).catch(() => {
      setNotice("Could not save your client view preference. It applies to this tab only.");
    });
  }, []);

  const recordRecentClient = useCallback((orgId: string) => {
    setRecentIds((list) => {
      if (list[0] === orgId) return list;
      const next = pushRecentClientId(user.id, list, orgId);
      if (recentSynced.current) void api.patch("/consultant/workspace/prefs", { recentClientIds: next }).catch(() => undefined);
      return next;
    });
  }, [user.id]);

  // Names come from the current session's client list (or the portfolio, whose
  // rows are the same organisations). An id in neither is skipped, and once
  // both lists have settled such ids are pruned from storage so they do not
  // come back on the next mount. `clientsLoaded` is set even when the light
  // list fails, so the portfolio is consulted too before anything is dropped.
  const recentClients = useMemo<RecentClient[]>(() => {
    const out: RecentClient[] = [];
    for (const id of recentIds) {
      const name = clients.find((c) => c.id === id)?.name ?? portfolio.orgs.find((o) => o.id === id)?.name;
      if (name) out.push({ id, name });
    }
    return out;
  }, [recentIds, clients, portfolio.orgs]);
  useEffect(() => {
    if (!clientsLoaded || !portfolio.loaded) return;
    if (clients.length === 0 && portfolio.orgs.length === 0) return;
    const known = new Set([...clients.map((c) => c.id), ...portfolio.orgs.map((o) => o.id)]);
    const stale = recentIds.filter((id) => !known.has(id));
    if (stale.length) setRecentIds((list) => forgetRecentClientIds(user.id, list, stale));
  }, [clientsLoaded, portfolio.loaded, clients, portfolio.orgs, recentIds, user.id]);

  // Switches this tab to a client session on the consultancy's own organisation
  // (POST /auth/select-role, as for the auditor portal). The consultant workspace
  // never renders that organisation's portal itself: there is exactly one way in.
  // Resolves false when the switch failed (the notice explains why), so the
  // caller can offer a retry instead of waiting.
  const enterOwnOrgPortal = useCallback(async (): Promise<boolean> => {
    setNotice(null);
    try {
      const data = await startRoleSwitch<{ accessToken: string }>("/auth/select-role", { role: "client" });
      setViewingOrgId(null); // the client session scopes itself; no consultant read-through org may linger
      navigate("/actions");
      await onSwitchRole?.();
      return true;
    } catch (e) {
      setNotice(e instanceof ApiError && e.code === "no_self_org"
        ? "Your consultancy has not registered its own organisation yet."
        : "Could not open your company's portal. Please try again.");
      return false;
    }
  }, [navigate, onSwitchRole]);

  // The auditor portal for a consultancy that owns an active bureau. Landing
  // on the auditor home before the session changes means no redirect flashes.
  const openAuditorPortal = useCallback(async () => {
    setNotice(null);
    try {
      const data = await startRoleSwitch<{ accessToken: string }>("/auth/select-role", { role: "auditor" });
      setViewingOrgId(null);
      navigate("/auditor/clients");
      await onSwitchRole?.();
    } catch {
      setNotice("Could not open the auditor portal. Your bureau may still be awaiting approval.");
    }
  }, [navigate, onSwitchRole]);

  // Called by SelfOrgSetupModal on success: straight into the new portal.
  const handleSelfOrgCreated = (orgId: string, orgName: string) => {
    setPortfolio((p) => ({ ...p, selfOrgId: orgId }));
    setClients((list) => (list.some((c) => c.id === orgId) ? list : [...list, { id: orgId, name: orgName, sector: null, createdAt: new Date().toISOString(), tier: "", frameworks: [], latestScore: null, userCount: 0 }]));
    setShowSelfOrgSetup(false);
    void enterOwnOrgPortal();
  };

  const openSelfOrgSetupDialog = useCallback(() => setShowSelfOrgSetup(true), []);
  const openInviteClient = useCallback(() => setShowInviteClient(true), []);
  const openInviteStaff = useCallback(() => setShowInviteStaff(true), []);
  const notify = useCallback((message: string) => setNotice(message), []);

  const value = useMemo<ConsultantShellValue>(() => ({
    user,
    permissions: user.permissions,
    clients,
    clientsLoaded,
    reloadClients,
    portfolio,
    reloadPortfolio,
    forgetClient,
    setSelfOrgId,
    selfOrg,
    recentClients,
    recordRecentClient,
    enterOwnOrgPortal,
    openAuditorPortal,
    openSelfOrgSetup: openSelfOrgSetupDialog,
    openInviteClient,
    openInviteStaff,
    notify,
    scope,
    setScope,
    isFirmAdmin: user.permissions.includes("firm:admin"),
  }), [
    user, clients, clientsLoaded, reloadClients, portfolio, reloadPortfolio, forgetClient, setSelfOrgId, selfOrg,
    recentClients, recordRecentClient, enterOwnOrgPortal, openAuditorPortal, openSelfOrgSetupDialog, openInviteClient, openInviteStaff, notify,
    scope, setScope,
  ]);

  return (
    <ConsultantShellContext.Provider value={value}>
      <Routes>
        <Route path="/consultant/clients/:orgId/portal/*" element={<ConsultantClientPortal />} />
        <Route path="/consultant/*" element={<ConsultantWorkspace />} />
        <Route path="*" element={<Navigate to={CONSULTANT_HOME} replace />} />
      </Routes>

      {notice && (
        <div
          role="alert"
          style={{
            position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", zIndex: 1200,
            background: theme.color.dangerBg, color: theme.color.dangerText, border: `1px solid ${theme.color.danger}`,
            borderRadius: 3, padding: "10px 14px", fontSize: 12.5, display: "flex", alignItems: "center", gap: 12,
            maxWidth: "calc(100vw - 32px)", fontFamily: theme.font,
          }}
        >
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 12 }}>
            Dismiss
          </button>
        </div>
      )}

      {showInviteClient && (
        <InviteClientModal canInviteJersey={user.canInviteJerseyEdition ?? false} onClose={() => { setShowInviteClient(false); reloadClients(); reloadPortfolio(); }} />
      )}
      {showInviteStaff && <InviteStaffModal onClose={() => setShowInviteStaff(false)} />}
      {showSelfOrgSetup && (
        <SelfOrgSetupModal
          onClose={() => setShowSelfOrgSetup(false)}
          onCreated={handleSelfOrgCreated}
        />
      )}
    </ConsultantShellContext.Provider>
  );
}
